'use client'
import {useLocale, useTranslations} from "next-intl";
import {
    Button,
    Carousel,
    Col,
    ConfigProvider,
    Divider,
    Flex, Image,
    Layout,
    Row,
    TabsProps,
    Typography
} from "antd";
import {Content} from "antd/lib/layout/layout";
import React, {CSSProperties, useEffect, useState} from "react";
import {ThemeService} from "@/src/services/theme";
import Loading from "../loading";
import {LoggerService} from "@/src/services/logger";
import {DataViewModel} from "@/src/viewModels/dataViewModel";
import {Subscription} from "rxjs";
import {LoaderService} from "@/src/services/loader";
import {LanguageEnum} from "@/src/entities/languageEnum";
import {Lodging, LodgingAttributes} from "@/src/entities/lodging";
import {useSearchParams} from "next/navigation";
import NotificationComponent, {NotificationObj} from '@/src/components/notification';
import Bathroom from "@/public/assets/portfolio/icons/attributes/toilette.svg"
import AirConditioner from "@/public/assets/portfolio/icons/attributes/airconditioner.svg"
import Pool from "@/public/assets/portfolio/icons/attributes/swimming.svg"
import Tv from "@/public/assets/portfolio/icons/attributes/tv.svg"
import Heating from "@/public/assets/portfolio/icons/attributes/heating.svg"
import Kitchen from "@/public/assets/portfolio/icons/attributes/kitchen.svg"
import Microwave from "@/public/assets/portfolio/icons/attributes/microwave.svg"
import Oven from "@/public/assets/portfolio/icons/attributes/oven.svg"
import Phon from "@/public/assets/portfolio/icons/attributes/phon.svg"
import Washer from "@/public/assets/portfolio/icons/attributes/washer.svg"
import Parking from "@/public/assets/portfolio/icons/attributes/parking.svg"
import Wifi from "@/public/assets/portfolio/icons/attributes/wifi.svg"
import Icon from "@ant-design/icons";
import styles from "@/src/app/[locale]/home/video.module.css";
import bed from "@/public/assets/portfolio/icons/bed.svg";
import bathroom from "@/public/assets/portfolio/icons/bathroom.svg";
import size from "@/public/assets/portfolio/icons/size.svg";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import {Constants} from "@/src/lib/constants";

const LodgingPage = () => {
    const localization = useTranslations('lodging');
    const generalLocalization = useTranslations('general');

    const themeColors = ThemeService.getThemeColors()
    const [isLoaded, setIsLoaded] = useState(false)
    const viewModel = new DataViewModel();

    let subscription: Subscription | null;
    const loaderService = LoaderService.getInstance();
    const locale = useLocale()
    const [lodging, setLodging] = useState<Lodging | undefined>(undefined)
    const lodgingId = useSearchParams().get("id")
    const [notificationState, setNotificationState] = useState<NotificationObj | null>(null);
    const [tabItems , setTabItems] = useState<TabsProps['items']>([])
    const breakpoint = useBreakpoint()
    const searchParams = useSearchParams()

    const iconStyle: CSSProperties = {
        fontSize: 32
    }

    const topIconStyle: CSSProperties = {
        fontSize: breakpoint.md ? 32 : 20
    }

    useEffect(() => {
        let language: LanguageEnum
        switch (locale) {
            case "it":
                language = LanguageEnum.ITALIAN
                break
            case "en":
                language = LanguageEnum.ENGLISH
                break
            default:
                language = LanguageEnum.ENGLISH
                break
        }
        subscription = viewModel.retrieveLodgingList(language).subscribe({
            next: value => {
                const lodg = value.find((val) => {
                    return val.id === lodgingId
                })
                setIsLoaded(true)
                setLodging(lodg)

                const tabI: TabsProps['items'] = [
                    {
                        key: '1',
                        label: localization("tab2Title"),
                        children:  <video
                            muted={true}
                            loop={false}
                            src={lodg?.video}
                            autoPlay={false}
                            controls
                            disablePictureInPicture
                            controlsList="nodownload noplaybackrate"
                            className={styles.Video}
                        />
                    },
                    {
                        key: '2',
                        label: localization("tab1Title"),
                        children: <Carousel adaptiveHeight={true} infinite={true} autoplay={false} dots={true} draggable={true} autoplaySpeed={5000} effect={"scrollx"}>
                            {(() => {
                                return lodg?.photos.map((value, index) => {
                                    return <Image preview={false} src={value} key={index}/>
                                })
                            })()}
                        </Carousel>,

                    }
                ];

                setTabItems(tabI)
            },
            error: error => {
                loaderService.hide()
                LoggerService.getLogger().debug(`error: ${JSON.stringify(error)}`)

                const parsedError = JSON.parse(JSON.stringify(error))
                const errorCode = error.error.statusCode;
                const errorMessage = error.error.message;

                const notificationObj = {
                    type: "error",
                    title: generalLocalization("error"),
                    description: errorMessage
                }
                setNotificationState(notificationObj)
            }
        })

        return () => {
            unsubscribeSubscription(subscription)
        };
    }, []);

    const unsubscribeSubscription = (subscription: Subscription | null) => {
        if (subscription != null) {
            subscription.unsubscribe();
            subscription = null
            LoggerService.getLogger().debug(`<<< UNSUSCRIBE ${subscription} SERVICE`)
        }
    }

    const getAttributeFragment = (attribute: LodgingAttributes, index: number) => {
        const iconPrefix = "/assets/portfolio/icons/attributes"
        let icon: React.ReactElement = <></>
        let title: string = ""
        switch (attribute) {
            case LodgingAttributes.PRIVATE_BATHROOM:
                icon = <Icon component={Bathroom} style={iconStyle} />
                title = localization("bathroom")
                break
            case LodgingAttributes.AIR_CONDITIONER:
                icon = <Icon component={AirConditioner} style={iconStyle} />
                title = localization("airConditioner")
                break
            case LodgingAttributes.HEATING:
                icon = <Icon component={Heating} style={iconStyle} />
                title = localization("heating")
                break
            case LodgingAttributes.KITCHEN:
                icon = <Icon component={Kitchen} style={iconStyle} />
                title = localization("kitchen")
                break
            case LodgingAttributes.FREE_PARKING:
                icon = <Icon component={Parking} style={iconStyle} />
                title = localization("parking")
                break
            case LodgingAttributes.PHON:
                icon = <Icon component={Phon} style={iconStyle} />
                title = localization("phon")
                break
            case LodgingAttributes.POOL:
                icon = <Icon component={Pool} style={iconStyle} />
                title = localization("pool")
                break
            case LodgingAttributes.TV:
                icon = <Icon component={Tv} style={iconStyle} />
                title = localization("tv")
                break
            case LodgingAttributes.MICROWAVE:
                icon = <Icon component={Microwave} style={iconStyle} />
                title = localization("microwave")
                break
            case LodgingAttributes.OVEN:
                icon = <Icon component={Oven} style={iconStyle} />
                title = localization("oven")
                break
            case LodgingAttributes.WIFI:
                icon = <Icon component={Wifi} style={iconStyle} />
                title = localization("wifi")
                break
            case LodgingAttributes.WASHING_MACHINE:
                icon = <Icon component={Washer} style={iconStyle} />
                title = localization("washer")
                break
            default: break
        }
        return <Col xs={12} sm={8} key={index} >
            <Flex vertical={false} align={"center"} justify={"flex-start"} gap={8} wrap>
            {icon}
            <Typography.Paragraph style={{margin: 0}}>{title}</Typography.Paragraph>
        </Flex>
        </Col>
    }

    const getLodgingShortFeatures = () => {
        return <Flex vertical={false} gap={30}>
            <Flex vertical={false} gap={4} align={"center"}>
                <Icon component={bed} style={topIconStyle} />
                <p>{`${lodging?.beds} ${localization("beds")}`}</p>
            </Flex>
            <Flex vertical={false} gap={4} align={"center"}>
                <Icon component={bathroom} style={topIconStyle} />
                <p>{`${lodging?.bathrooms} ${localization("bathrooms")}`}</p>
            </Flex>
            <Flex vertical={false} gap={4} align={"center"}>
                <Icon component={size} style={topIconStyle} />
                <p>{`${lodging?.size} ${localization("size")}`}</p>
            </Flex>
        </Flex>
    }

    return <Layout>
        {(() => {
            if (isLoaded) {
                return <ConfigProvider
                    theme={{
                        components: {
                            Tabs: {
                                itemSelectedColor: themeColors.menuBarColor,
                                itemHoverColor: themeColors.primary,
                                itemActiveColor: themeColors.menuBarColor,
                                inkBarColor: themeColors.menuBarColor
                            }
                        }
                    }}>
                    <Content style={{ padding: "20px 16px 48px 16px", backgroundColor: `${themeColors.tertiary}` }}>
                        <NotificationComponent notificationState={notificationState} setNotificationState={setNotificationState} />

                        <Row gutter={16}>
                            <Col span={breakpoint.lg ? 14 : 24} offset={breakpoint.lg ? 5 : 0}>
                                <Row gutter={16} justify={"space-around"}>
                                    <Col span={24}>
                                        <Flex justify={"flex-end"} style={{marginBottom: 30}}>
                                            <Button size={"large"} color={"danger"} variant={"outlined"} onClick={(event)=>{
                                                event.stopPropagation()
                                                const id = searchParams.get('id')
                                                let link: string = ""
                                                switch (id) {
                                                    case "lodging1":
                                                        link = Constants.happyRentalLodging1Link
                                                        break
                                                    case "lodging2":
                                                        link = Constants.happyRentalLodging2Link
                                                        break
                                                    case "lodging3":
                                                        link = Constants.happyRentalsLink
                                                        break
                                                    case "lodging4":
                                                        link = Constants.happyRentalLodging4Link
                                                        break
                                                }
                                                window.open(link, "_blank")
                                            }}>{localization("reservation")}</Button>
                                        </Flex>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        {(()=> {
                                            if (!breakpoint.md) {
                                                return <Flex vertical align={"center"} style={{marginBottom: 30}}>
                                                    {getLodgingShortFeatures()}
                                                </Flex>
                                            }
                                        })()}
                                        <Flex vertical={false} align={"start"} justify={"space-between"}   >
                                            <Flex vertical>
                                                <Typography.Title level={3}>{lodging?.name}</Typography.Title>
                                                <Divider style={{marginTop: 5, backgroundColor: ThemeService.getThemeColors().menuBarColor}} />
                                            </Flex>
                                            {(()=> {
                                                if (breakpoint.md) {
                                                    return getLodgingShortFeatures()
                                                }
                                            })()}
                                        </Flex>

                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col xs={24} >
                                        {/*<Tabs items={tabItems} />*/}
                                            <video
                                                muted={true}
                                                loop={false}
                                                src={lodging?.video}
                                                autoPlay={false}
                                                controls
                                                disablePictureInPicture
                                                controlsList="nodownload noplaybackrate"
                                                className={styles.Video}
                                                style={{minWidth: "100%"}}
                                            />
                                    </Col>
                                </Row>
                                <Row gutter={16} style={{marginTop: 40}}>
                                    <Col>
                                        <Typography.Title level={3}>{localization("description")}</Typography.Title>
                                        <Divider style={{marginTop: 5, backgroundColor: ThemeService.getThemeColors().menuBarColor}} />
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col>
                                        <Typography.Paragraph style={{fontSize: 16}}>{lodging?.description}</Typography.Paragraph>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={24} style={{border: "1px solid transparent", borderRadius: 10, overflow:"hidden"}}>
                                        <Carousel adaptiveHeight={true} infinite={true} autoplay={false} dots={true} draggable={true} autoplaySpeed={5000} effect={"scrollx"}>
                                            {(() => {
                                                return lodging?.photos.map((value, index) => {
                                                    return <Image preview={false} src={value} key={index}/>
                                                })
                                            })()}
                                        </Carousel>
                                    </Col>
                                </Row>
                                <Row gutter={16} style={{marginTop: 40}}>
                                    <Col>
                                        <Typography.Title level={3}>{localization("features")}</Typography.Title>
                                        <Divider style={{marginTop: 5, backgroundColor: ThemeService.getThemeColors().menuBarColor}} />
                                    </Col>
                                </Row>
                                <Row gutter={[30, 20]}>
                                    {(() => {
                                        return lodging?.attributes.map((value, index) => {
                                            return getAttributeFragment(value, index)
                                        })
                                    })()}
                                </Row>
                            </Col>
                        </Row>
                    </Content>
                </ConfigProvider>
            } else {
                return <Loading />
            }
        })()}
    </Layout>
}

export default LodgingPage

